import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Button,
  FormActions,
  FormLayout,
  FormRow,
  FormSection,
  Select,
  TextInput,
} from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import {
  EMPLOYER_OWNERSHIP_STRUCTURE_OPTIONS,
  EMPLOYER_TYPE_OPTIONS,
  emptyEmployerRegistrationForm,
} from './employerInfoConstants';
import { employerInfoSchema, type EmployerInfoValues } from './employerInfoSchema';

const TAGS = CT.employer.registration;
const EMPLOYER_EIN_TAG = 'employer-registration-employer-ein';

interface Props {
  defaultValues?: EmployerInfoValues;
  onSubmit: (data: EmployerInfoValues) => void;
  submitLabel?: string;
  loading?: boolean;
}

export const EmployerInfoForm = ({
  defaultValues = emptyEmployerRegistrationForm,
  onSubmit,
  submitLabel = 'Submit',
  loading = false,
}: Props) => {
  const methods = useCustomForm<EmployerInfoValues>({
    resolver: zodResolver(employerInfoSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const onSubmitError = (err: unknown) =>
    console.log('There was an error submitting the Employer Info form', err);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <TextInput
                cyTag={TAGS.legalEntityName}
                name="legalEntityName"
                label="Legal Entity Name"
                required
              />
            </FormRow>
            <FormRow>
              <TextInput cyTag={EMPLOYER_EIN_TAG} name="employerEin" label="EIN" />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <TextInput cyTag={TAGS.employerNpi} name="employerNpi" label="NPI" />
            </FormRow>
            <FormRow>
              <Select
                cyTag={TAGS.employerType}
                name="employerType"
                label="Organization Type"
                options={EMPLOYER_TYPE_OPTIONS}
                required
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                cyTag={TAGS.ownershipStructure}
                name="ownershipStructure"
                label="Ownership Structure"
                options={EMPLOYER_OWNERSHIP_STRUCTURE_OPTIONS}
                required
              />
            </FormRow>
            <FormRow>
              <TextInput cyTag={TAGS.employerWebsite} name="employerWebsite" label="Website" />
            </FormRow>
          </FormSection>
          <FormActions sticky={false}>
            <Button
              cyTag={TAGS.submitButton}
              label={loading ? 'Saving...' : submitLabel}
              type="submit"
              disabled={loading}
            />
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
